// Typed wrappers over the admin API — one function per operation the screens need.
// Keeps components free of URL/verb details and gives the tests a single seam.

import { api } from "./client.js"
import type {
  AdminContext,
  OrgShopDto,
  OrganizationDto,
  PermissionDto,
  RoleDetailDto,
  RoleDto,
  TeamDetailDto,
  TeamDto,
  UserDto,
  UserTeamSummaryDto,
  UsersPageDto
} from "./types.js"

export interface CreateRoleBody {
  name: string
  displayName?: string
  description?: string | null
  organizationId: string
}

export interface UpdateRoleBody {
  displayName?: string
  description?: string | null
}

export interface CreateTeamBody {
  name: string
  description?: string | null
  organizationId: string
}

export interface UpdateTeamBody {
  name?: string
  description?: string | null
}

export const adminApi = {
  context: () => api.get<AdminContext>("/context"),

  listRoles: (org?: string) =>
    api.get<RoleDto[]>(org ? `/roles?org=${encodeURIComponent(org)}` : "/roles"),
  getRole: (id: string) => api.get<RoleDetailDto>(`/roles/${encodeURIComponent(id)}`),
  createRole: (body: CreateRoleBody) => api.post<RoleDetailDto>("/roles", body),
  updateRole: (id: string, body: UpdateRoleBody) =>
    api.patch<RoleDetailDto>(`/roles/${encodeURIComponent(id)}`, body),
  deleteRole: (id: string) => api.del<{ status: string }>(`/roles/${encodeURIComponent(id)}`),
  setRolePermissions: (id: string, permissionIds: string[]) =>
    api.put<RoleDetailDto>(`/roles/${encodeURIComponent(id)}/permissions`, { permissionIds }),

  listPermissions: () => api.get<PermissionDto[]>("/permissions"),

  listOrganizations: () => api.get<OrganizationDto[]>("/organizations"),
  listUsers: (org: string) => api.get<UserDto[]>(`/users?org=${encodeURIComponent(org)}`),

  /** A server-side page of users-with-roles (search + pagination on the backend). */
  listUsersWithRoles: (params: { org: string; page: number; pageSize: number; q?: string }) => {
    const qs = new URLSearchParams({
      org: params.org,
      page: String(params.page),
      pageSize: String(params.pageSize)
    })
    if (params.q?.trim()) qs.set("q", params.q.trim())
    return api.get<UsersPageDto>(`/users-with-roles?${qs.toString()}`)
  },

  listUserRoles: (userId: string, org: string) =>
    api.get<RoleDto[]>(
      `/user-roles?userId=${encodeURIComponent(userId)}&org=${encodeURIComponent(org)}`
    ),
  /** The teams a user belongs to within an org (read-only, chips on the detail page). */
  listUserTeams: (userId: string, org: string) =>
    api.get<UserTeamSummaryDto[]>(
      `/user-teams?userId=${encodeURIComponent(userId)}&org=${encodeURIComponent(org)}`
    ),
  assignUserRole: (body: { userId: string; roleId: string; organizationId: string }) =>
    api.post<{ status: string }>("/user-roles", body),
  removeUserRole: (body: { userId: string; roleId: string; organizationId: string }) =>
    api.del<{ status: string }>("/user-roles", body),

  // ── Shops (the `shops` claim's write side + the org's shop catalog) ──────────
  /** The org's canonical shop catalog for the picker (proxied from solya-app). */
  listOrgShops: () => api.get<OrgShopDto[]>("/org-shops"),
  /** A user's DIRECTLY-assigned shop ids in an org. */
  listUserShops: (userId: string, org: string) =>
    api.get<string[]>(
      `/user-shops?userId=${encodeURIComponent(userId)}&org=${encodeURIComponent(org)}`
    ),
  /** Full-replacement of a user's direct shops in an org. */
  setUserShops: (body: { userId: string; organizationId: string; shopIds: string[] }) =>
    api.put<{ status: string; shopIds: string[] }>("/user-shops", body),
  /** A team's assigned shop ids. */
  listTeamShops: (teamId: string) =>
    api.get<string[]>(`/teams/${encodeURIComponent(teamId)}/shops`),
  /** Full-replacement of a team's shops. */
  setTeamShops: (teamId: string, shopIds: string[]) =>
    api.put<{ status: string; shopIds: string[] }>(`/teams/${encodeURIComponent(teamId)}/shops`, {
      shopIds
    }),

  // ── Teams ──────────────────────────────────────────────────────────────────
  listTeams: (org: string) => api.get<TeamDto[]>(`/teams?org=${encodeURIComponent(org)}`),
  getTeam: (id: string) => api.get<TeamDetailDto>(`/teams/${encodeURIComponent(id)}`),
  createTeam: (body: CreateTeamBody) => api.post<TeamDetailDto>("/teams", body),
  updateTeam: (id: string, body: UpdateTeamBody) =>
    api.patch<TeamDetailDto>(`/teams/${encodeURIComponent(id)}`, body),
  deleteTeam: (id: string) => api.del<{ status: string }>(`/teams/${encodeURIComponent(id)}`),
  addTeamMember: (teamId: string, userId: string) =>
    api.post<TeamDetailDto>(
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`
    ),
  removeTeamMember: (teamId: string, userId: string) =>
    api.del<TeamDetailDto>(
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`
    ),
  assignTeamRole: (teamId: string, roleId: string) =>
    api.post<TeamDetailDto>(
      `/teams/${encodeURIComponent(teamId)}/roles/${encodeURIComponent(roleId)}`
    ),
  unassignTeamRole: (teamId: string, roleId: string) =>
    api.del<TeamDetailDto>(
      `/teams/${encodeURIComponent(teamId)}/roles/${encodeURIComponent(roleId)}`
    )
}
